// Every thread Oreia has found, not just the two the dashboard has room for.
//
// The pipeline has been writing these nightly since linking was switched on, and until now the only
// way to see one was to catch it in the top two of a dashboard card. Nothing here is newly generated;
// this is a reading problem, and the rows are the ones already in the database.
//
// Grouped by the month the thread was found rather than by the entries' own dates, because a thread
// is a thing Oreia noticed on a particular night — its two ends can be a fortnight apart, so neither
// of their dates is the date of the observation.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSession } from '../auth/SessionProvider';
import { useEntries } from '../entries/EntriesProvider';
import { EmptyState, Spinner } from '../components/ui';
import { IconLock } from '../components/Icons';
import { fetchLinks, resolveLinks, type EntryLink, type ResolvedLink } from '../api/links';
import { ThreadRow } from './ThreadRow';

/**
 * More than the endpoint's own default, since this screen is the one place that claims to show
 * everything. The server caps it well below anything that would be slow to decrypt.
 */
const HISTORY_LIMIT = 200;

const MONTH = new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' });

export function ThreadsScreen() {
  const { vault } = useSession();
  const dek = vault.status === 'unlocked' ? vault.dek : null;
  const { entries, isLoading: entriesLoading } = useEntries();

  const [links, setLinks] = useState<EntryLink[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!dek) return;
    const controller = new AbortController();
    setFailed(false);
    setLinks(null);

    fetchLinks(dek, { limit: HISTORY_LIMIT, signal: controller.signal })
      .then((rows) => {
        if (!controller.signal.aborted) setLinks(rows);
      })
      .catch(() => {
        if (!controller.signal.aborted) setFailed(true);
      });

    return () => controller.abort();
  }, [dek]);

  const threads = useMemo(() => resolveLinks(links ?? [], entries), [links, entries]);
  const months = useMemo(() => groupByMonth(threads), [threads]);
  const loading = !failed && (links === null || entriesLoading);

  return (
    <>
      <div className="ohead">
        <div className="obrand">
          <span className="nm" style={{ fontSize: 22 }}>Threads</span>
        </div>
        <div className="olock">
          <IconLock /> Private
        </div>
      </div>

      <div className="ojhead">
        <h1>
          What your dreams
          <br />
          <em className="oitalic">keep working on.</em>
        </h1>
        <p className="ojsub">
          {loading
            ? 'Looking through what Oreia has connected…'
            : threads.length === 0
              ? 'No connections yet — they appear as dreams and waking entries start to rhyme.'
              : `${threads.length} ${threads.length === 1 ? 'connection' : 'connections'} Oreia has found`}
        </p>
      </div>

      {loading ? (
        <Spinner label="Decrypting your threads…" />
      ) : failed ? (
        <EmptyState
          title="Couldn't reach your threads."
          body="The connection dropped on the way. Refreshing usually sorts it."
        />
      ) : threads.length === 0 ? (
        <EmptyState
          title="Nothing connected yet."
          body="Oreia looks for dreams that seem to be working on something you wrote about awake. Keep a dream or two alongside your journal and the threads start to show."
          action={<Link className="obtn" to="/write">Write something</Link>}
        />
      ) : (
        months.map(([month, rows]) => (
          <div className="ocard ocard-light" key={month}>
            <div className="ohead-row">
              <span className="olabel">{month}</span>
              <span className="omono">{rows.length}</span>
            </div>
            {rows.map((thread) => (
              <ThreadRow key={thread.id} thread={thread} />
            ))}
          </div>
        ))
      )}
    </>
  );
}

/**
 * Newest month first, threads inside each in the order the endpoint gave them (also newest first).
 *
 * A thread with no creation date sorts into "Earlier" rather than being dropped: the row is still
 * perfectly readable, and losing an insight over a missing timestamp would be the wrong trade.
 */
export function groupByMonth(threads: ResolvedLink[]): [string, ResolvedLink[]][] {
  const groups = new Map<string, ResolvedLink[]>();

  for (const thread of threads) {
    const when = thread.createdAt ? new Date(thread.createdAt) : null;
    const key = when && !Number.isNaN(when.getTime()) ? MONTH.format(when) : 'Earlier';
    const bucket = groups.get(key);
    if (bucket) bucket.push(thread);
    else groups.set(key, [thread]);
  }

  // Insertion order already follows the endpoint's newest-first sort, so no re-sort is needed — and
  // re-sorting on the formatted month string would order them alphabetically, which is worse.
  return [...groups.entries()];
}
